import { ChevronLeftIcon, Save, X } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { useNavigate, useLocation } from "react-router-dom";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { Input, Textarea } from "../../components/ui/input";
import { Checkbox, Select, TimePicker } from "antd";
import { Button } from "../../components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { cn } from "../../utils/tailwindUtils";
import { leaveTypeConfigEnm, leavePolicyExpiryEnum } from "../../constants/enum";
import { EMPLOYEE_LEAVE_POLICY } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { configBranchLabel, withConfigBranch } from "../../utils/branchSession";
import dayjs from "dayjs";

const isLeavePolicyType = (type) => type && type !== "permission" && type !== "work_from_home";

const EmployeeLeavePolicyAddEdit = () => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        policy_code: "",
        request_type: undefined,
        policy_name: "",
        no_of_days: "",
        selected_time: null,
        leave_period: undefined,
        carry_forward: false,
        attendance_eligibility_enabled: false,
        min_present_days: "15",
        attendance_lookback: "previous_month",
        include_wfh_as_present: false,
        max_consecutive_days: "",
        document_required_after_days: "",
        allowed_genders: [],
        min_service_months: "",
        work_on_leave_pay_enabled: false,
        work_on_leave_amount: "",
        work_on_leave_pay_frequency: "",
        leave_encashment_enabled: false,
        leave_encashment_amount: "",
        leave_encashment_max_days: "",
        notes: "",
        is_active: true,
        company_id: ""
    });

    const { create, update } = useApiMethods();
    const navigate = useNavigate();
    const location = useLocation();
    const { fromEdit, editDetails, isActive } = location.state || {};

    // Permission gating — guards direct navigation to the add/edit route.
    const { add: addPermission, edit: editPermission, draft: draftPermission } =
        checkUserPermissionFunc("master", "Leave Type", "SubMenu");
    const canSave = fromEdit ? editPermission : addPermission;

    useEffect(() => {
        if (!canSave) navigate("/master/leavetype", { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSave]);
    const companyid = sessionStorage.getItem("userId");

    // Initialize form data
    useEffect(() => {
        if (fromEdit && editDetails) {
            setFormData({
                policy_code: editDetails.code || "",
                request_type: editDetails.request_type === "general" ? "leave" : (editDetails.request_type || undefined),
                policy_name: editDetails.leave_name || "",
                no_of_days: editDetails.no_of_days?.toString() || "",
                selected_time: editDetails.no_of_hours ? dayjs(editDetails.no_of_hours, "HH:mm:ss") : null,
                leave_period: editDetails.leave_expiry || undefined,
                carry_forward: editDetails.leave_carry_forward || false,
                attendance_eligibility_enabled: editDetails.attendance_eligibility_enabled || false,
                min_present_days: editDetails.min_present_days?.toString() || "15",
                attendance_lookback: editDetails.attendance_lookback || "previous_month",
                include_wfh_as_present: editDetails.include_wfh_as_present || false,
                max_consecutive_days: editDetails.max_consecutive_days != null ? String(editDetails.max_consecutive_days) : "",
                document_required_after_days: editDetails.document_required_after_days != null ? String(editDetails.document_required_after_days) : "",
                allowed_genders: Array.isArray(editDetails.allowed_genders) ? editDetails.allowed_genders : [],
                min_service_months: editDetails.min_service_months != null ? String(editDetails.min_service_months) : "",
                work_on_leave_pay_enabled: Boolean(editDetails.work_on_leave_pay_enabled),
                work_on_leave_amount: editDetails.work_on_leave_amount != null && editDetails.work_on_leave_amount !== ""
                    ? String(editDetails.work_on_leave_amount)
                    : "",
                work_on_leave_pay_frequency:
                    editDetails.work_on_leave_pay_enabled
                    && (editDetails.work_on_leave_pay_frequency === "monthly" || editDetails.work_on_leave_pay_frequency === "yearly")
                        ? editDetails.work_on_leave_pay_frequency
                        : "",
                leave_encashment_enabled: Boolean(editDetails.leave_encashment_enabled),
                leave_encashment_amount: editDetails.leave_encashment_amount != null && editDetails.leave_encashment_amount !== ""
                    ? String(editDetails.leave_encashment_amount)
                    : "",
                leave_encashment_max_days: editDetails.leave_encashment_max_days != null && editDetails.leave_encashment_max_days !== ""
                    ? String(editDetails.leave_encashment_max_days)
                    : "",
                notes: editDetails.notes || "",
                is_active: editDetails.is_active ?? true,
                company_id: editDetails.company_id || companyid
            });
        } else {
            setFormData(prev => ({
                ...prev,
                company_id: companyid
            }));
        }
    }, [fromEdit, editDetails, companyid]);

    const handleBack = () => {
        navigate('/master/leavetype');
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            if (field === "work_on_leave_pay_enabled" && !value) {
                return {
                    ...prev,
                    work_on_leave_pay_enabled: false,
                    work_on_leave_pay_frequency: "",
                    work_on_leave_amount: "",
                };
            }
            if (field === "leave_encashment_enabled" && !value) {
                return {
                    ...prev,
                    leave_encashment_enabled: false,
                    leave_encashment_amount: "",
                    leave_encashment_max_days: "",
                };
            }
            if (field === "work_on_leave_pay_frequency" && value) {
                return {
                    ...prev,
                    work_on_leave_pay_frequency: value,
                };
            }
            return {
                ...prev,
                [field]: value
            };
        });
    };

    const toggleGender = (gender) => {
        setFormData(prev => {
            const current = Array.isArray(prev.allowed_genders) ? prev.allowed_genders : [];
            const next = current.includes(gender)
                ? current.filter((item) => item !== gender)
                : [...current, gender];
            return { ...prev, allowed_genders: next };
        });
    };

    // Validation function
    const validateForm = () => {
        if (!formData.request_type) {
            ToastMsg("error", "Request Type is required!");
            return false;
        }

        if (isLeavePolicyType(formData.request_type) && !formData.policy_name) {
            ToastMsg("error", "Leave Policy Name is required!");
            return false;
        }

        if ((isLeavePolicyType(formData.request_type) || formData.request_type === "work_from_home") && !formData.no_of_days) {
            ToastMsg("error", "No of Days is required!");
            return false;
        }

        if (formData.request_type === "permission" && !formData.selected_time) {
            ToastMsg("error", "Select Time is required!");
            return false;
        }

        if (!formData.leave_period) {
            ToastMsg("error", "Leave Period is required!");
            return false;
        }

        if (isLeavePolicyType(formData.request_type) && formData.attendance_eligibility_enabled) {
            const minDays = parseInt(formData.min_present_days, 10);
            if (!minDays || minDays < 1) {
                ToastMsg("error", "Minimum present days is required when attendance eligibility is enabled!");
                return false;
            }
        }

        if (isLeavePolicyType(formData.request_type)) {
            const consecutive = formData.max_consecutive_days === "" ? null : parseInt(formData.max_consecutive_days, 10);
            if (formData.max_consecutive_days !== "" && (!consecutive || consecutive < 1)) {
                ToastMsg("error", "Maximum consecutive days must be 1 or more, or left blank.");
                return false;
            }
            const documentAfter = formData.document_required_after_days === "" ? null : parseInt(formData.document_required_after_days, 10);
            if (formData.document_required_after_days !== "" && (Number.isNaN(documentAfter) || documentAfter < 0)) {
                ToastMsg("error", "Document required after days must be 0 or more, or left blank.");
                return false;
            }
            const serviceMonths = formData.min_service_months === "" ? null : parseInt(formData.min_service_months, 10);
            if (formData.min_service_months !== "" && (!serviceMonths || serviceMonths < 1)) {
                ToastMsg("error", "Minimum service months must be 1 or more, or left blank.");
                return false;
            }
            if (formData.work_on_leave_pay_enabled) {
                const amount = parseFloat(formData.work_on_leave_amount);
                if (!amount || amount <= 0) {
                    ToastMsg("error", "Enter the amount to pay for a day worked in leave days.");
                    return false;
                }
                if (formData.work_on_leave_pay_frequency !== "monthly" && formData.work_on_leave_pay_frequency !== "yearly") {
                    ToastMsg("error", "Choose Monthly or Yearly. You can check only one.");
                    return false;
                }
            }
            if (formData.leave_encashment_enabled) {
                const encashAmount = parseFloat(formData.leave_encashment_amount);
                if (!encashAmount || encashAmount <= 0) {
                    ToastMsg("error", "Enter the amount to pay per unused leave day.");
                    return false;
                }
                if (formData.leave_encashment_max_days !== "") {
                    const maxDays = parseInt(formData.leave_encashment_max_days, 10);
                    if (!maxDays || maxDays < 1) {
                        ToastMsg("error", "Maximum days to encash must be 1 or more, or left blank.");
                        return false;
                    }
                }
            }
        }

        return true;
    };

    const handleSave = async (isDraft = false) => {
        if (isDraft ? !draftPermission : !canSave) {
            ToastMsg("error", "You don't have permission to perform this action");
            return;
        }
        if (!isDraft && !validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Prepare payload
            const payload = withConfigBranch({
                code: formData.policy_code || null,
                request_type: formData.request_type,
                leave_name: isLeavePolicyType(formData.request_type) ? formData.policy_name : null,
                no_of_days: formData.no_of_days ? parseInt(formData.no_of_days) : 0,
                no_of_hours: formData.request_type === 'permission' && formData.selected_time
                    ? formData.selected_time.format("HH:mm:ss")
                    : '00:00:00',
                notes: formData.notes || '',
                leave_expiry: formData.leave_period ? parseInt(formData.leave_period) : null,
                leave_carry_forward: formData.carry_forward,
                attendance_eligibility_enabled: isLeavePolicyType(formData.request_type) && Boolean(formData.attendance_eligibility_enabled),
                min_present_days: formData.min_present_days ? parseInt(formData.min_present_days, 10) : 15,
                attendance_lookback: formData.attendance_lookback || "previous_month",
                include_wfh_as_present: Boolean(formData.include_wfh_as_present),
                max_consecutive_days: isLeavePolicyType(formData.request_type) && formData.max_consecutive_days !== ""
                    ? parseInt(formData.max_consecutive_days, 10)
                    : null,
                document_required_after_days: isLeavePolicyType(formData.request_type) && formData.document_required_after_days !== ""
                    ? parseInt(formData.document_required_after_days, 10)
                    : null,
                allowed_genders: isLeavePolicyType(formData.request_type) ? (formData.allowed_genders || []) : [],
                min_service_months: isLeavePolicyType(formData.request_type) && formData.min_service_months !== ""
                    ? parseInt(formData.min_service_months, 10)
                    : null,
                work_on_leave_pay_enabled: isLeavePolicyType(formData.request_type) && Boolean(formData.work_on_leave_pay_enabled),
                work_on_leave_amount: isLeavePolicyType(formData.request_type) && formData.work_on_leave_pay_enabled && formData.work_on_leave_amount !== ""
                    ? parseFloat(formData.work_on_leave_amount)
                    : null,
                work_on_leave_pay_frequency: isLeavePolicyType(formData.request_type) && formData.work_on_leave_pay_enabled
                    && (formData.work_on_leave_pay_frequency === "monthly" || formData.work_on_leave_pay_frequency === "yearly")
                    ? formData.work_on_leave_pay_frequency
                    : null,
                leave_encashment_enabled: isLeavePolicyType(formData.request_type) && Boolean(formData.leave_encashment_enabled),
                leave_encashment_amount: isLeavePolicyType(formData.request_type) && formData.leave_encashment_enabled && formData.leave_encashment_amount !== ""
                    ? parseFloat(formData.leave_encashment_amount)
                    : null,
                leave_encashment_max_days: isLeavePolicyType(formData.request_type) && formData.leave_encashment_enabled && formData.leave_encashment_max_days !== ""
                    ? parseInt(formData.leave_encashment_max_days, 10)
                    : null,
                is_active: formData.is_active,
                company_id: formData.company_id,
                is_draft: isDraft
            });

            // Add ID for edit mode
            if (fromEdit && editDetails?.id) {
                payload.id = editDetails.id;
            }

            let response;
            if (fromEdit) {
                response = await update(EMPLOYEE_LEAVE_POLICY, payload);
            } else {
                response = await create(EMPLOYEE_LEAVE_POLICY, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `Leave policy ${fromEdit ? 'updated' : 'created'} successfully`);
                handleBack();
            } else {
                ToastMsg("error", response?.message || `Failed to ${fromEdit ? 'update' : 'create'} leave policy`);
            }
        } catch (error) {
            console.error('Error saving leave policy:', error);
            ToastMsg("error", "An error occurred while saving the leave policy");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAsDraft = useCallback(() => {
        handleSave(true);
    }, [formData]);

    const handleCancel = () => {
        handleBack();
    };

    // Number validation for no_of_days
    const handleNumberInput = (e) => {
        const value = e.target.value;
        if (value === "" || (parseInt(value) >= 1 && parseInt(value) <= 30)) {
            handleInputChange('no_of_days', value);
        }
    };

    const handleKeyDown = (e) => {
        if (
            !/^[\d.]$/.test(e.key) &&
            e.key !== "Backspace" &&
            e.key !== "Delete" &&
            e.key !== "ArrowLeft" &&
            e.key !== "ArrowRight" &&
            e.key !== "Home" &&
            e.key !== "End"
        ) {
            e.preventDefault();
        }
    };

    // Options for dropdowns
    const requestTypeOptions = leaveTypeConfigEnm.map(option => ({
        value: option.value,
        label: option.text
    }));

    const leavePeriodOptions = leavePolicyExpiryEnum.map(option => ({
        value: option.value,
        label: option.text
    }));

    // Check if form has data for draft button
    const hasFormData = Object.values(formData).some(value =>
        value !== "" && value !== undefined && value !== null && value !== false
    );

    return (
        <ContainerCard scrollable>
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={handleBack} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {fromEdit ? "Edit Leave Policy" : "Add Leave Policy"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Define leave and time-off policies for {configBranchLabel()}.
                    </p>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    {/* <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Active Status
                    </label> */}
                    <div className="flex items-center gap-3">
                        <span className={cn(
                            "text-sm font-medium",
                            formData.is_active
                                ? "text-green-600 dark:text-green-400"
                                : "text-gray-500 dark:text-gray-400"
                        )}>
                            {formData.is_active ? "Active" : "Inactive"}
                        </span>
                        <button
                            type="button"
                            onClick={() => handleInputChange('is_active', !formData.is_active)}
                            className={cn(
                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                !fromEdit && !isActive ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                            )}
                            disabled={!fromEdit && !isActive}
                        >
                            <span
                                className={cn(
                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                    formData.is_active ? "translate-x-4" : "translate-x-1"
                                )}
                            />
                        </button>
                    </div>
                </div>
            </div>

            {/* Form Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Leave Policy Code - Only show in edit mode */}
                    {fromEdit && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Leave Policy Code <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formData.policy_code}
                                onChange={(e) => handleInputChange('policy_code', e.target.value)}
                                placeholder="Enter policy code"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                )}
                                disabled={fromEdit}
                            />
                        </div>
                    )}

                    {/* Request Type */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Request Type <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.request_type}
                            onChange={(value) => handleInputChange('request_type', value)}
                            options={requestTypeOptions}
                            className="w-full"
                            size="large"
                            placeholder="Select request type"
                            allowClear
                            disabled={fromEdit}
                        />
                    </div>

                    {/* Conditionally render fields based on request type */}
                    {isLeavePolicyType(formData.request_type) && (
                        <>
                            {/* Leave Policy Name */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Leave Policy Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    value={formData.policy_name}
                                    onChange={(e) => handleInputChange('policy_name', e.target.value)}
                                    placeholder="Enter policy name"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                    disabled={formData.policy_name === "Work From Home"}
                                />
                            </div>

                            {/* No of Days */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Number of Days <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="number"
                                    value={formData.no_of_days}
                                    onChange={handleNumberInput}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Enter number of days"
                                    min="1"
                                    max="30"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                        "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                    )}
                                />
                            </div>
                        </>
                    )}

                    {formData.request_type === "work_from_home" && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Number of Days <span className="text-red-500">*</span>
                            </label>
                            <Input
                                type="number"
                                value={formData.no_of_days}
                                onChange={handleNumberInput}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter number of days"
                                min="1"
                                max="30"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                )}
                            />
                        </div>
                    )}

                    {formData.request_type === "permission" && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select Time <span className="text-red-500">*</span>
                            </label>
                            <TimePicker
                                value={formData.selected_time}
                                onChange={(time) => handleInputChange('selected_time', time)}
                                format="HH:mm"
                                className={cn(
                                    "w-full rounded-md transition-colors duration-200",
                                    "border-gray-300 dark:border-gray-600",
                                    "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                )}
                                placeholder="Select time"
                                size="large"
                            />
                        </div>
                    )}

                    {/* Leave Period */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Leave Period <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formData.leave_period}
                            onChange={(value) => handleInputChange('leave_period', value)}
                            options={leavePeriodOptions}
                            className="w-full"
                            size="large"
                            placeholder="Select leave period"
                            allowClear
                        />
                    </div>

                    {/* Carry Forward Checkbox - Full width */}
                    <div className="md:col-span-2 flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                        <Checkbox
                            id="carry_forward"
                            checked={formData.carry_forward}
                            onChange={(e) => handleInputChange('carry_forward', e.target.checked)}
                        />
                        <label htmlFor="carry_forward" className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                            Allow Carry Forward to Next Period
                        </label>
                    </div>

                    {isLeavePolicyType(formData.request_type) && (
                        <div className="md:col-span-2 space-y-4 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="attendance_eligibility_enabled"
                                    checked={formData.attendance_eligibility_enabled}
                                    onChange={(e) => handleInputChange('attendance_eligibility_enabled', e.target.checked)}
                                />
                                <label htmlFor="attendance_eligibility_enabled" className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Require minimum present days to apply
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                When enabled, employees can submit this leave only if their present days in the selected window are more than the number below (Attendance Log worked days: present, on-duty, compensation).
                            </p>
                            {formData.attendance_eligibility_enabled && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Minimum present days <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            type="number"
                                            value={formData.min_present_days}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === "" || parseInt(value, 10) >= 1) {
                                                    handleInputChange("min_present_days", value);
                                                }
                                            }}
                                            placeholder="15"
                                            min="1"
                                            className={cn(
                                                "w-full rounded-md transition-colors duration-200",
                                                "border-gray-300 dark:border-gray-600",
                                            )}
                                        />
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            Example: 15 means more than 15 present days. Use 20 for stricter mandatory attendance.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Count window
                                        </label>
                                        <Select
                                            value={formData.attendance_lookback}
                                            onChange={(value) => handleInputChange("attendance_lookback", value)}
                                            options={[
                                                { value: "previous_month", label: "Previous month" },
                                                { value: "current_month", label: "Current month" },
                                                { value: "rolling_30", label: "Last 30 days" },
                                            ]}
                                            className="w-full"
                                            size="large"
                                        />
                                    </div>
                                    <div className="md:col-span-2 flex items-center gap-3">
                                        <Checkbox
                                            id="include_wfh_as_present"
                                            checked={formData.include_wfh_as_present}
                                            onChange={(e) => handleInputChange("include_wfh_as_present", e.target.checked)}
                                        />
                                        <label htmlFor="include_wfh_as_present" className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Count work from home as present
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {isLeavePolicyType(formData.request_type) && (
                        <div className="md:col-span-2 space-y-4 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                            <div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Restrictions</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Optional. Leave a field blank or unchecked to keep this policy open. Examples: Casual = max 3 consecutive days, Sick = document after 2 days, Maternity = female + minimum service months.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Maximum consecutive days
                                    </label>
                                    <Input
                                        type="number"
                                        value={formData.max_consecutive_days}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "" || parseInt(value, 10) >= 1) {
                                                handleInputChange("max_consecutive_days", value);
                                            }
                                        }}
                                        placeholder="e.g. 3"
                                        min="1"
                                        className={cn(
                                            "w-full rounded-md transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                        )}
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Blank means no cap. 3 blocks a 4-day Casual Leave request.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Document required after days
                                    </label>
                                    <Input
                                        type="number"
                                        value={formData.document_required_after_days}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "" || parseInt(value, 10) >= 0) {
                                                handleInputChange("document_required_after_days", value);
                                            }
                                        }}
                                        placeholder="e.g. 2"
                                        min="0"
                                        className={cn(
                                            "w-full rounded-md transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                        )}
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        2 means a medical document is needed when leave is more than 2 days. 0 means a document is always required.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Minimum service months
                                    </label>
                                    <Input
                                        type="number"
                                        value={formData.min_service_months}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "" || parseInt(value, 10) >= 1) {
                                                handleInputChange("min_service_months", value);
                                            }
                                        }}
                                        placeholder="e.g. 12"
                                        min="1"
                                        className={cn(
                                            "w-full rounded-md transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                        )}
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Counted from date of joining to the leave start date. Blank means no service bar.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Allowed genders
                                    </label>
                                    <div className="flex flex-wrap gap-4 pt-2">
                                        {[
                                            { value: "male", label: "Male" },
                                            { value: "female", label: "Female" },
                                            { value: "others", label: "Others" },
                                        ].map((option) => (
                                            <div key={option.value} className="flex items-center gap-2">
                                                <Checkbox
                                                    id={`allowed_gender_${option.value}`}
                                                    checked={(formData.allowed_genders || []).includes(option.value)}
                                                    onChange={() => toggleGender(option.value)}
                                                />
                                                <label htmlFor={`allowed_gender_${option.value}`} className="cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                                                    {option.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Leave all unchecked to allow every gender. Check Female only for Maternity.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {isLeavePolicyType(formData.request_type) && (
                        <div className="md:col-span-2 space-y-4 p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    id="work_on_leave_pay_enabled"
                                    checked={formData.work_on_leave_pay_enabled}
                                    onChange={(e) => handleInputChange("work_on_leave_pay_enabled", e.target.checked)}
                                />
                                <label htmlFor="work_on_leave_pay_enabled" className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Pay for days worked in leave days
                                </label>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                If the employee punches in (Present / On-duty / Compensation) on approved leave days, payroll adds this amount as a credit. Half-day leave pays half.
                            </p>
                            <div className={cn("space-y-2", !formData.work_on_leave_pay_enabled && "opacity-60")}>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Add this money in {formData.work_on_leave_pay_enabled && <span className="text-red-500">*</span>}
                                </label>
                                <div className="flex flex-wrap gap-6">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="work_on_leave_pay_monthly"
                                            checked={formData.work_on_leave_pay_frequency === "monthly"}
                                            disabled={!formData.work_on_leave_pay_enabled}
                                            onChange={(e) => handleInputChange(
                                                "work_on_leave_pay_frequency",
                                                e.target.checked ? "monthly" : ""
                                            )}
                                        />
                                        <label
                                            htmlFor="work_on_leave_pay_monthly"
                                            className={cn(
                                                "text-sm text-gray-700 dark:text-gray-300",
                                                formData.work_on_leave_pay_enabled ? "cursor-pointer" : "cursor-not-allowed"
                                            )}
                                        >
                                            Monthly payroll
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="work_on_leave_pay_yearly"
                                            checked={formData.work_on_leave_pay_frequency === "yearly"}
                                            disabled={!formData.work_on_leave_pay_enabled}
                                            onChange={(e) => handleInputChange(
                                                "work_on_leave_pay_frequency",
                                                e.target.checked ? "yearly" : ""
                                            )}
                                        />
                                        <label
                                            htmlFor="work_on_leave_pay_yearly"
                                            className={cn(
                                                "text-sm text-gray-700 dark:text-gray-300",
                                                formData.work_on_leave_pay_enabled ? "cursor-pointer" : "cursor-not-allowed"
                                            )}
                                        >
                                            Yearly payroll (January)
                                        </label>
                                    </div>
                                </div>
                                {formData.work_on_leave_pay_enabled && formData.work_on_leave_pay_frequency === "yearly" && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        All days worked in leave days from last year are paid on the January payroll.
                                    </p>
                                )}
                                {formData.work_on_leave_pay_enabled && formData.work_on_leave_pay_frequency === "monthly" && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Days worked in leave days during that month are paid on that month's payroll.
                                    </p>
                                )}
                            </div>
                            <div className={cn("space-y-2 max-w-sm", !formData.work_on_leave_pay_enabled && "opacity-60")}>
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Amount per day worked in leave days (₹) {formData.work_on_leave_pay_enabled && <span className="text-red-500">*</span>}
                                </label>
                                <Input
                                    type="number"
                                    value={formData.work_on_leave_amount}
                                    disabled={!formData.work_on_leave_pay_enabled}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "" || parseFloat(value) >= 0) {
                                            handleInputChange("work_on_leave_amount", value);
                                        }
                                    }}
                                    placeholder="e.g. 500"
                                    min="0"
                                    step="0.01"
                                    className={cn(
                                        "w-full rounded-md transition-colors duration-200",
                                        "border-gray-300 dark:border-gray-600",
                                    )}
                                />
                            </div>
                            <div className="border-t border-gray-200 dark:border-gray-600 pt-4 space-y-4">
                                <div className="flex items-center gap-3">
                                    <Checkbox
                                        id="leave_encashment_enabled"
                                        checked={formData.leave_encashment_enabled}
                                        onChange={(e) => handleInputChange("leave_encashment_enabled", e.target.checked)}
                                    />
                                    <label htmlFor="leave_encashment_enabled" className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Encash unused leave
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Unused days of this leave type from the previous calendar year are paid on the January payroll. Days the employee worked during leave stay on the option above. A loss-of-pay leave is not encashed.
                                </p>
                                <div className={cn("space-y-2 max-w-sm", !formData.leave_encashment_enabled && "opacity-60")}>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Amount per unused day (₹) {formData.leave_encashment_enabled && <span className="text-red-500">*</span>}
                                    </label>
                                    <Input
                                        type="number"
                                        value={formData.leave_encashment_amount}
                                        disabled={!formData.leave_encashment_enabled}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "" || parseFloat(value) >= 0) {
                                                handleInputChange("leave_encashment_amount", value);
                                            }
                                        }}
                                        placeholder="e.g. 500"
                                        min="0"
                                        step="0.01"
                                        className="w-full rounded-md border-gray-300 dark:border-gray-600"
                                    />
                                </div>
                                <div className={cn("space-y-2 max-w-sm", !formData.leave_encashment_enabled && "opacity-60")}>
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Maximum days to encash
                                    </label>
                                    <Input
                                        type="number"
                                        value={formData.leave_encashment_max_days}
                                        disabled={!formData.leave_encashment_enabled}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "" || (parseInt(value, 10) >= 1)) {
                                                handleInputChange("leave_encashment_max_days", value);
                                            }
                                        }}
                                        placeholder="Blank pays every unused day"
                                        min="1"
                                        step="1"
                                        className="w-full rounded-md border-gray-300 dark:border-gray-600"
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Leave this blank to pay the whole unused balance. A number such as 15 pays at most 15 days.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Notes - Full width */}
                    <div className="md:col-span-2 space-y-2">
                        <label className={modalFieldLabelClass}>
                            Notes
                        </label>
                        <Textarea
                            value={formData.notes}
                            onChange={(e) => handleInputChange('notes', e.target.value)}
                            placeholder="Enter additional notes or description"
                            showCharCount={true}
                            maxLength={500}
                            rows={4}
                            className={modalTextareaClass}
                        />
                    </div>
                </div>

                {/* Required Fields Note */}
                <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                        <span className="text-red-500">*</span> indicates required fields
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={loading}
                        className="flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Cancel
                    </Button>

                    {/* Draft Button - Only show in create mode with draft permission */}
                    {!fromEdit && draftPermission && (
                        <Button
                            onClick={handleSaveAsDraft}
                            disabled={loading || !hasFormData}
                            className={cn(
                                "bg-gray-600 hover:bg-gray-700",
                                "text-white transition-colors duration-200",
                                "flex items-center gap-2",
                                (!hasFormData) && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            Save as Draft
                        </Button>
                    )}

                    <Button
                        variant="brand"
                        onClick={() => handleSave(false)}
                        disabled={loading || !canSave}
                        className="flex items-center gap-2 transition-colors duration-200"
                    >
                        {loading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <Save className="w-4 h-4" />
                        )}
                        {loading ? "Saving..." : (fromEdit ? "Update" : "Save Policy")}
                    </Button>
                </div>
            </div>
        </ContainerCard>
    );
};

export default EmployeeLeavePolicyAddEdit;